import { notify } from '../utils/notify';
import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/Modal';
import { Lock, Pencil, Save, X } from 'lucide-react';
import './MyProfile.css';

const MyProfile = () => {
  const { user, updateUser } = useContext(AuthContext);
  const { t, locale } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: ''
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await authAPI.updateProfile(profileForm);
      updateUser(response.data.user);
      notify(t('Profile updated successfully!'), 'success');
      setEditing(false);
    } catch (error) {
      notify(error.response?.data?.message || t('Error updating profile'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      notify(t('New passwords do not match!'), 'warning');
      return;
    }

    try {
      setLoading(true);
      await authAPI.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      notify(t('Password changed successfully!'), 'success');
      setShowPasswordModal(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      notify(error.response?.data?.message || t('Error changing password'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="my-profile-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('My Profile')}</h1>
          <p className="page-sub">{t('Manage your personal information')}</p>
        </div>
      </div>

      <div className="mp-layout">
        {/* Profile Info Card */}
        <div className="card mp-card">
          <div className="mp-who">
            <div className="mp-avatar">
              {user?.profile_picture ? (
                <img src={user.profile_picture} alt="Profile" />
              ) : (
                <div className="mp-avatar__initials">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
              )}
            </div>
            <div className="mp-who__info">
              <h2>{user?.first_name} {user?.last_name}</h2>
              <p className="status status--neutral mp-role">{user?.role}</p>
              <p className="mp-who__email">{user?.email}</p>
            </div>
          </div>

          <div className="mp-actions">
            {!editing ? (
              <button onClick={() => setEditing(true)} className="btn btn--primary">
                <Pencil aria-hidden="true" /> {t('Edit Profile')}
              </button>
            ) : (
              <button onClick={() => setEditing(false)} className="btn btn--secondary">
                <X aria-hidden="true" /> {t('Cancel')}
              </button>
            )}
            <button onClick={() => setShowPasswordModal(true)} className="btn btn--secondary">
              <Lock aria-hidden="true" /> {t('Change Password')}
            </button>
          </div>
        </div>

        {/* Edit Form */}
        {editing && (
          <div className="card mp-card">
            <h3 className="mp-card__title">{t('Edit Information')}</h3>
            <form onSubmit={handleUpdateProfile}>
              <div className="mp-form-row">
                <div className="mp-field">
                  <label className="field-label">{t('First Name')}</label>
                  <input className="input"
                    type="text"
                    value={profileForm.first_name}
                    onChange={(e) => setProfileForm({...profileForm, first_name: e.target.value})}
                    required
                  />
                </div>
                <div className="mp-field">
                  <label className="field-label">{t('Last Name')}</label>
                  <input className="input"
                    type="text"
                    value={profileForm.last_name}
                    onChange={(e) => setProfileForm({...profileForm, last_name: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="mp-field">
                <label className="field-label">{t('Phone')}</label>
                <input className="input"
                  type="tel"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                />
              </div>

              <div className="mp-field">
                <label className="field-label">{t('Email (Read-only)')}</label>
                <input className="input"
                  type="email"
                  value={profileForm.email}
                  disabled
                />
              </div>

              <div className="mp-form-actions">
                <button type="submit" className="btn btn--primary" disabled={loading}>
                  {loading ? t('Saving...') : <><Save aria-hidden="true" /> {t('Save Changes')}</>}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Account Info */}
        <div className="card mp-card">
          <h3 className="mp-card__title">{t('Account Information')}</h3>
          <div className="mp-info">
            <div className="mp-info__row">
              <span className="mp-info__label">{t('Account Created:')}</span>
              <span className="mp-info__value">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString(locale) : t('N/A')}
              </span>
            </div>
            <div className="mp-info__row">
              <span className="mp-info__label">{t('Last Login:')}</span>
              <span className="mp-info__value">
                {user?.last_login ? new Date(user.last_login).toLocaleString(locale) : t('N/A')}
              </span>
            </div>
            <div className="mp-info__row">
              <span className="mp-info__label">{t('Role:')}</span>
              <span className="mp-info__value">{user?.role}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <Modal open title={t('Change Password')} onClose={() => setShowPasswordModal(false)} size="md">

            <form onSubmit={handleChangePassword} className="mp-dialog">
              <div className="mp-field">
                <label className="field-label">{t('Current Password')}</label>
                <input className="input"
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                  required
                />
              </div>

              <div className="mp-field">
                <label className="field-label">{t('New Password')}</label>
                <input className="input"
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                  minLength="8"
                  required
                />
              </div>

              <div className="mp-field">
                <label className="field-label">{t('Confirm New Password')}</label>
                <input className="input"
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                  minLength="8"
                  required
                />
              </div>

              <div className="mp-form-actions">
                <button type="button" onClick={() => setShowPasswordModal(false)} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary" disabled={loading}>
                  {loading ? t('Changing...') : t('Change Password')}
                </button>
              </div>
            </form>
        </Modal>
      )}
    </div>
  );
};

export default MyProfile;